import React, { useEffect, useState } from 'react';
import { ListDairyInsurancePriceDatesResponse, ListDairyInsurancePricesResponse } from '../../../../../Generated/Raven-Demeter';
import informationSvg from '../../../../Assets/Icons/info.svg';
import PageAccordion from '../../../../Components/Form/Buttons/Accordions/PageAccordion';
import LinkButton, { LinkButtonType } from '../../../../Components/Form/Buttons/LinkButton';
import NumberInput from '../../../../Components/Form/Inputs/NumberInput';
import TextInput from '../../../../Components/Form/Inputs/TextInput';
import ComponentSubHeader from '../../../../Components/Headers/ComponentSubHeader';
import RegexValidators from '../../../../Core/Validation/RegexValidators';
import useLanguage from '../../../../Services/Language/useLanguageHook';
import GeneralRiskDateDropdown from '../CommonFields/GeneralRiskDateDropdown';
import GeneralRiskInsuranceStateDropdown from '../CommonFields/GeneralRiskInsuranceStateDropdown';
import GeneralRiskInsuranceYearDropdown from '../CommonFields/GeneralRiskInsuranceYearDropdown';
import styles from './DairyRevenueProtection.module.scss';
import {
    DairyRevenueProtectionCalculationInputs,
    DairySelectionTableRow,
    defaultButterFatPercentage,
    defaultClass3MilkPercent,
    defaultClass4MilkPercent,
    defaultProteinPercentage,
    GeneralRiskCalculationInputs,
    getQuarter,
} from './DairyRevenueProtectionDefinitions';
import DairyRevenueProtectionCoverageLevelDropdown from './DairyRevenueProtectionFields.tsx/DairyRevenueProtectionCoverageLevelDropdown';
import DairyRevenueProtectionDeclaredProductionInput from './DairyRevenueProtectionFields.tsx/DairyRevenueProtectionDeclaredProductionInput';
import DairyRevenueProtectionDeclaredShareInput from './DairyRevenueProtectionFields.tsx/DairyRevenueProtectionDeclaredShareInput';
import DairyRevenueProtectionFactorDropdown from './DairyRevenueProtectionFields.tsx/DairyRevenueProtectionFactorDropdown';
import DairyRevenueProtectionSelectionsTable from './DairyRevenueProtectionSelectionsTable';

interface IDairyRevenueProtectionFormProps {
    tableCalculations: boolean;
    setTableCalculations: (tableCalculations: boolean) => void;
    calculationInputs: DairyRevenueProtectionCalculationInputs | undefined;
    setCalculatorInputs: (value: DairyRevenueProtectionCalculationInputs) => void;
    setCalculatedTableRows: (value: DairySelectionTableRow[]) => void;
    refreshListDairyInsurancePrices: () => void;
    listDairyInsurancePricesResponse: ListDairyInsurancePricesResponse;
    listDairyInsurancePriceDatesResponse: ListDairyInsurancePriceDatesResponse;
}

const DairyRevenueProtectionForm: React.FC<IDairyRevenueProtectionFormProps> = (props: IDairyRevenueProtectionFormProps) => {
    const [translations] = useLanguage();
    const [selectedTableRows, setSelectedTableRows] = useState<DairySelectionTableRow[]>([]);
    const [selectAll, setSelectAll] = useState<boolean>(false);

    useEffect(() => {
        props.refreshListDairyInsurancePrices();
    }, [props.calculationInputs]);

    useEffect(() => {
        // Since all the fields for both tables are on the same api, we need to check specifically
        // the first table fields.
        const onlyCalculationsChanged =
            selectedTableRows.length === props.listDairyInsurancePricesResponse?.rows?.length &&
            selectedTableRows.every((x, index) => {
                const currentRow = props.listDairyInsurancePricesResponse?.rows![index];
                return (
                    x.class3MilkPrice === currentRow.class3MilkPrice &&
                    x.class4MilkPrice === currentRow.class4MilkPrice &&
                    x.butterfatPrice === currentRow.butterfatPrice &&
                    x.proteinPrice === currentRow.proteinPrice &&
                    x.otherSolidsPrice === currentRow.otherSolidsPrice &&
                    x.nonfatSolidsPrice === currentRow.nonfatSolidsPrice &&
                    x.expectedYield === currentRow.expectedYield
                );
            });

        if (onlyCalculationsChanged || !props.listDairyInsurancePricesResponse?.rows) {
            return;
        }

        const rows = props.listDairyInsurancePricesResponse.rows.map((x) => ({
            ...x,
            title: getQuarter(x),
            isSelected: false,
        }));

        setSelectAll(false);
        props.setCalculatedTableRows(
            rows.map((x) => {
                const newTableRows = { ...x };
                delete (newTableRows as DairySelectionTableRow).actions;
                return newTableRows;
            }),
        );
        setSelectedTableRows(rows as DairySelectionTableRow[]);
    }, [props.listDairyInsurancePricesResponse]);

    useEffect(() => {
        const newValues: DairyRevenueProtectionCalculationInputs = {} as DairyRevenueProtectionCalculationInputs;
        if (props.calculationInputs?.classBasePercent && !props.calculationInputs.class3MilkPercent) {
            newValues.class3MilkPercent = defaultClass3MilkPercent;
            newValues.class4MilkPercent = defaultClass4MilkPercent;
        }

        if (props.calculationInputs?.componentBasedPercent && !props.calculationInputs?.fatPercent) {
            newValues.fatPercent = defaultButterFatPercentage;
            newValues.proteinPercent = defaultProteinPercentage;
        }

        props.setCalculatorInputs({ ...props.calculationInputs, ...newValues });
    }, [props.calculationInputs?.classBasePercent, props.calculationInputs?.componentBasedPercent]);

    const handleRunCalculator = () => {
        props.refreshListDairyInsurancePrices();
        props.setTableCalculations(!props.tableCalculations);
        const newCaclulatedTableRows = [...selectedTableRows!];
        props.setCalculatedTableRows(
            newCaclulatedTableRows.map((x) => {
                const newTableRows = { ...x };
                delete (newTableRows as DairySelectionTableRow).actions;
                return newTableRows;
            }),
        );
    };

    return (
        <div className={styles.insurances_quote_estimator}>
            <div className={styles.dairy_revenue_protection_section}>
                <PageAccordion
                    title={translations.risk.insuranceQuoteEstimator.headers.dairyRevenueProtectionForm}
                    open
                    loading={false}
                    component={
                        <>
                            <div className={styles.liquidity_risk_section}>
                                <div className={styles.liquidity_risk_row}>
                                    <div className={styles.liquidity_risk_blue_box}>
                                        <img src={informationSvg} alt="#" /> {translations.risk.insuranceQuoteEstimator.text.fillOutTheForm}{' '}
                                    </div>
                                </div>
                            </div>
                            <div className={styles.dairy_revenue_protection_row}>
                                <GeneralRiskInsuranceYearDropdown
                                    calculationInputs={props.calculationInputs!}
                                    setCalculatorInputs={props.setCalculatorInputs as (value: GeneralRiskCalculationInputs) => void}
                                    listLivestockInsurancePriceDatesResponse={props.listDairyInsurancePriceDatesResponse!}
                                />
                                <GeneralRiskInsuranceStateDropdown
                                    calculationInputs={props.calculationInputs!}
                                    setCalculatorInputs={props.setCalculatorInputs as (value: GeneralRiskCalculationInputs) => void}
                                />
                                <GeneralRiskDateDropdown
                                    calculationInputs={props.calculationInputs!}
                                    setCalculatorInputs={props.setCalculatorInputs as (value: GeneralRiskCalculationInputs) => void}
                                    listLivestockInsurancePriceDatesResponse={props.listDairyInsurancePriceDatesResponse!}
                                />
                            </div>
                            <div className={styles.dairy_revenue_protection_row}>
                                <DairyRevenueProtectionCoverageLevelDropdown
                                    calculationInputs={props.calculationInputs!}
                                    setCalculatorInputs={props.setCalculatorInputs}
                                    listDairyInsurancePricesResponse={props.listDairyInsurancePricesResponse}
                                />
                                <DairyRevenueProtectionFactorDropdown
                                    calculationInputs={props.calculationInputs!}
                                    setCalculatorInputs={props.setCalculatorInputs}
                                />
                                <DairyRevenueProtectionDeclaredProductionInput
                                    calculationInputs={props.calculationInputs!}
                                    setCalculatorInputs={props.setCalculatorInputs}
                                />
                            </div>
                            <div className={styles.dairy_revenue_protection_row}>
                                <DairyRevenueProtectionDeclaredShareInput
                                    calculationInputs={props.calculationInputs!}
                                    setCalculatorInputs={props.setCalculatorInputs}
                                />
                            </div>
                            <div className={styles.dairy_revenue_protection_row}>
                                <h4 className={styles.dairy_revenue_protection_section_header}>{translations.risk.margin.headers.forgetToSelect}</h4>
                            </div>
                            <DairyRevenueProtectionSelectionsTable
                                selectAll={selectAll}
                                setSelectAll={setSelectAll}
                                selectedTableRows={selectedTableRows}
                                setSelectedTableRows={setSelectedTableRows}
                            />
                            <div className={styles.dairy_revenue_protection_row}>
                                <ComponentSubHeader
                                    title={translations.risk.margin.headers.priceOptionInformation}
                                    containerClassName={styles.dairy_revenue_protection_header}
                                />
                            </div>
                            <div className={styles.dairy_revenue_protection_row}>
                                <div className={styles.dairy_revenue_protection_fit_content}>
                                    <h4 className={styles.dairy_revenue_protection_section_header}>{translations.risk.margin.headers.classBasedInsurance}</h4>

                                    <div className={styles.dairy_revenue_protection_block_row}>
                                        <TextInput
                                            title={translations.risk.margin.fields.classBasedPercent}
                                            type="number"
                                            required
                                            value={`${props.calculationInputs?.classBasePercent}`}
                                            validation={RegexValidators.PositiveNumberZeroThroughOneHundred}
                                            handleTextChange={(value) => {
                                                if (+value < 0 || +value > 100) {
                                                    return;
                                                }

                                                props.setCalculatorInputs({
                                                    ...props.calculationInputs!,
                                                    componentBasedPercent: 100 - +value,
                                                    classBasePercent: +value,
                                                });
                                            }}
                                            errorMessage={translations.marketIndicatorsManagement.messages.positiveNumberZeroThroughOneHundred}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'DairyClassBasedPercentChanged',
                                                eventValue: props.calculationInputs?.classBasePercent,
                                            }}
                                        />
                                    </div>
                                    <div className={styles.dairy_revenue_protection_block_row}>
                                        <TextInput
                                            title={translations.risk.margin.fields.class3Percent}
                                            required
                                            disabled={!props.calculationInputs?.classBasePercent}
                                            value={`${props.calculationInputs?.class3MilkPercent}`}
                                            validation={RegexValidators.PositiveNumberZeroThroughOneHundred}
                                            handleTextChange={(value) => {
                                                if (+value < 0 || +value > 100) {
                                                    return;
                                                }

                                                props.setCalculatorInputs({
                                                    ...props.calculationInputs!,
                                                    class3MilkPercent: +value,
                                                    class4MilkPercent: 100 - +value,
                                                });
                                            }}
                                            errorMessage={translations.marketIndicatorsManagement.messages.positiveNumberZeroThroughOneHundred}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'DairyClass3MilkPercentChanged',
                                                eventValue: props.calculationInputs?.class3MilkPercent,
                                            }}
                                        />
                                    </div>
                                    <div className={styles.dairy_revenue_protection_block_row}>
                                        <TextInput
                                            title={translations.risk.margin.fields.class4Percent}
                                            disabled={!props.calculationInputs?.classBasePercent}
                                            required
                                            value={`${props.calculationInputs?.class4MilkPercent}`}
                                            validation={RegexValidators.PositiveNumberZeroThroughOneHundred}
                                            handleTextChange={(value) => {
                                                if (+value < 0 || +value > 100) {
                                                    return;
                                                }

                                                props.setCalculatorInputs({
                                                    ...props.calculationInputs!,
                                                    class3MilkPercent: 100 - +value,
                                                    class4MilkPercent: +value,
                                                });
                                            }}
                                            errorMessage={translations.marketIndicatorsManagement.messages.positiveNumberZeroThroughOneHundred}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'DairyClass4MilkPercentChanged',
                                                eventValue: props.calculationInputs?.class4MilkPercent,
                                            }}
                                        />
                                    </div>
                                </div>

                                <div className={styles.dairy_revenue_protection_fit_content}>
                                    <h4 className={styles.dairy_revenue_protection_section_header}>
                                        {translations.risk.margin.headers.componentBasedInsurance}
                                    </h4>

                                    <div className={styles.dairy_revenue_protection_block_row}>
                                        <TextInput
                                            title={translations.risk.margin.fields.componentBasedPercent}
                                            required
                                            value={`${props.calculationInputs?.componentBasedPercent}`}
                                            validation={RegexValidators.PositiveNumberZeroThroughOneHundred}
                                            handleTextChange={(value) => {
                                                if (+value < 0 || +value > 100) {
                                                    return;
                                                }

                                                props.setCalculatorInputs({
                                                    ...props.calculationInputs!,
                                                    componentBasedPercent: +value,
                                                    classBasePercent: 100 - +value,
                                                });
                                            }}
                                            errorMessage={translations.marketIndicatorsManagement.messages.positiveNumberZeroThroughOneHundred}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'DairyComponentBasedPercentChanged',
                                                eventValue: props.calculationInputs?.componentBasedPercent,
                                            }}
                                        />
                                    </div>
                                    <div className={styles.dairy_revenue_protection_block_row}>
                                        <NumberInput
                                            title={translations.risk.margin.fields.butterfatPercentage}
                                            required
                                            disabled={!props.calculationInputs?.componentBasedPercent}
                                            value={props.calculationInputs?.fatPercent}
                                            validation={RegexValidators.PositiveNumberFourThroughSix}
                                            handleTextChange={(value) => {
                                                if (value < 4 || value > 6) {
                                                    return;
                                                }

                                                props.setCalculatorInputs({
                                                    ...props.calculationInputs!,
                                                    fatPercent: +value,
                                                });
                                            }}
                                            errorMessage={translations.risk.text.fatPercentageMessage}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'DairyFatPercentChanged',
                                                eventValue: props.calculationInputs?.fatPercent,
                                            }}
                                        />
                                    </div>
                                    <div className={styles.dairy_revenue_protection_block_row}>
                                        <NumberInput
                                            title={translations.risk.margin.fields.proteinPercentage}
                                            required
                                            disabled={!props.calculationInputs?.componentBasedPercent}
                                            value={props.calculationInputs?.proteinPercent}
                                            validation={RegexValidators.PositiveNumberThreePointTwoThroughFourPointFive}
                                            handleTextChange={(value) => {
                                                if (value < 3.2 || value > 4.5) {
                                                    return;
                                                }

                                                props.setCalculatorInputs({
                                                    ...props.calculationInputs!,
                                                    proteinPercent: +value,
                                                });
                                            }}
                                            errorMessage={translations.risk.text.proteinPercentageMessage}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'DairyProteinPercentChanged',
                                                eventValue: props.calculationInputs?.proteinPercent,
                                            }}
                                        />
                                    </div>
                                </div>
                                <div className={styles.dairy_revenue_protection_fit_content}>
                                    <h4 className={styles.dairy_revenue_protection_section_header}>
                                        {translations.risk.margin.fields.declaredComonentPriceWeighting}
                                    </h4>
                                    <div className={styles.dairy_revenue_protection_block_row}>
                                        <NumberInput
                                            required
                                            disabled={!props.calculationInputs?.componentBasedPercent}
                                            value={props.calculationInputs?.componentWeightingFactorPercent}
                                            validation={RegexValidators.PositiveNumberZeroThroughOneHundred}
                                            handleTextChange={(value) => {
                                                if (value < 0 || value > 100) {
                                                    return;
                                                }

                                                props.setCalculatorInputs({
                                                    ...props.calculationInputs!,
                                                    componentWeightingFactorPercent: +value,
                                                });
                                            }}
                                            errorMessage={translations.marketIndicatorsManagement.messages.positiveNumberZeroThroughOneHundred}
                                            track={{
                                                eventCategory: 'InsuranceQuoteEstimator',
                                                eventAction: 'DairyComponentWeightingFactorPercentChanged',
                                                eventValue: props.calculationInputs?.componentWeightingFactorPercent,
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className={styles.dairy_revenue_protection_row}>
                                <LinkButton
                                    disabled={
                                        !props.calculationInputs?.declaredMilkProduction ||
                                        !props.calculationInputs.declaredSharePercent ||
                                        selectedTableRows.length === 0
                                    }
                                    title={translations.words.calculate}
                                    type={LinkButtonType.Blue}
                                    onClick={handleRunCalculator}
                                    track={{ eventCategory: 'InsuranceQuoteEstimator', eventAction: 'CalculateClicked' }}
                                />
                            </div>
                        </>
                    }
                />
            </div>
        </div>
    );
};

export default DairyRevenueProtectionForm;
